import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modifier, TimeSlot } from '../../../domain/schedule';
import { Ailment, Pill as TPill, StatusEffect } from '../../../domain/status';
import { useDrop } from 'react-dnd';
import { createUseStyles } from 'react-jss';
import { DraggablePill, DragOrigin, Pill } from '../Pills/Pill';


const ailmentIcon = (ailment: Ailment): string => {
    switch (ailment.key) {
        case 'ailment.insomnia':
            return '🥱';
        case 'ailment.stomachache':
            return '🤮';
        case 'ailment.headache':
            return '🤕';
        case 'ailment.sickness':
            return '🤒';
    }
    return '';
};

export const DragTargetTypes = {
    PILL: 'pill',
};

interface IDayTimeProps {
    index: number;
    isActive: boolean;
    isActivated: boolean;
    timeSlot: TimeSlot;
    statusEffects: Ailment[];
    selectedPills: TPill['key'][];
    availablePills: TPill[];
    onDrop: (timeSlot: TimeSlot, pill: TPill, origin: DragOrigin) => void;
    disabled: boolean;
}

const useStyles = createUseStyles<string, {
    canDrop: boolean;
    isOver: boolean;
    isActive: boolean;
    disabled: boolean;
}>({
    root: {
        minHeight: 185,
        display: 'flex',
        flexDirection: 'column',
    },
    pills: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 0 auto',
        position: 'relative',
        '&::after': {
            content: '""',
            display: 'block',
            top: '50%',
            left: '50%',
            width: '50%',
            height: '50%',
            transition: 'opacity 0.1s ease-in-out',
            background: 'radial-gradient(ellipse closest-side at 50% 50%, rgba(55, 181, 255, 0.4) 0%, rgba(255, 0, 0, 0) 100%)',
            transform: 'translate(-50%, -50%)',
            position: 'absolute',
            opacity: data => data.isOver && data.canDrop ? 0.6 : 0,
            zIndex: -1,
        },
    },
    status: {
        flex: '0 1 auto',
        fontSize: '1.3rem',
    },
});

export const DayTime: React.FunctionComponent<IDayTimeProps> = props => {
    const { t } = useTranslation();

    const selectedPillsRef = useRef<TPill['key'][]>([]);
    selectedPillsRef.current = props.selectedPills;

    const [ { canDrop, isOver }, drop ] = useDrop<DraggablePill, unknown, {
        canDrop: boolean;
        isOver: boolean;
    }>(() => ({
        accept: DragTargetTypes.PILL,
        drop: item => {
            props.onDrop(props.timeSlot, item.pill, item.origin);
        },
        collect: monitor => {
            return ({
                isOver: monitor.isOver(),
                canDrop: !props.disabled && !selectedPillsRef.current.includes(monitor.getItem()?.pill?.key),
            });
        },
        canDrop: (item) => {
            return !props.disabled && item.origin !== props.timeSlot.time && !selectedPillsRef.current.includes(item.pill.key);
        },
    }));

    const styles = useStyles({
        canDrop,
        isOver,
        isActive: props.isActive,
        disabled: props.disabled,
    });

    return (
        <div className={styles.root} ref={drop}>
            <span style={{ display: 'block' }}>{t(`timer.time.${props.timeSlot.time}`)}</span>
            <div aria-hidden className={styles.pills}>
                {props.selectedPills.map(pkey => {
                    const pill = props.availablePills.find(p => p.key === pkey);
                    if (!pill) return null;
                    return (
                        <Pill key={pkey} pill={pill} origin={props.timeSlot.time}/>
                    );
                })}
            </div>
            <div className={styles.status}>
                {props.timeSlot.modifiers.includes(Modifier.isMealtime) && '🍔'}
                {props.timeSlot.modifiers.includes(Modifier.isNight) && '🌛'}
                {props.statusEffects.map(s => (
                    <React.Fragment key={s.key}>{ailmentIcon(s)}</React.Fragment>
                ))}
            </div>
        </div>
    );
};
